/** Staff Accounting Services: QuickBooks connection, transaction review, 1099 compliance, the close checklist and the month-end close grid. */
import { formatCents, parseDollars } from "@clientflow/shared";
import { ask } from "@/components/dialogs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, CircleDashed, Copy, ExternalLink, RefreshCw, Sparkles, XCircle } from "lucide-react";
import { useState } from "react";
import { Link, useSearch } from "wouter";
import { Empty, ListSearch, Loading, matches, PageHeader, Pager, Section, Segmented, StatRow, usePaged } from "@/components/page";
import { AdjustingEntries } from "@/components/adjusting";
import { BooksStatements } from "@/components/books-statements";
import { Compliance1099, QboAlerts } from "@/components/compliance";
import { BookkeepingLists, BookkeepingRegister, QboChanges } from "@/components/bookkeeping";
import { BillsPayables } from "@/components/payables";
import { InvoicingReceivables } from "@/components/receivables";
import { Reconciliations } from "@/components/reconciliations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime, formatPeriod } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { TableScroll } from "@/components/table-scroll";

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";

type QboStatus = {
  connected: boolean;
  available: boolean;
  status?: "connected" | "needs_reconnect" | "disconnected";
  companyName?: string | null;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  refreshExpiresAt?: string | null;
  accounts?: Array<{ id: string; qboId: string; name: string; fullyQualifiedName: string; accountType: string; active: boolean; isSuspense: boolean; currentBalanceCents: number | null }>;
};

/** Last month, as the default range for pulling questions. */
function lastMonth() {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const last = new Date(d.getFullYear(), d.getMonth(), 0);
  const iso = (x: Date) => x.toLocaleDateString("en-CA");
  return { start: iso(first), end: iso(last) };
}

export function QboConnection({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const search = new URLSearchParams(useSearch());
  const result = search.get("qbo");
  const { data, isLoading } = useQuery<QboStatus>({ queryKey: ["/api/clients/", clientId, "/qbo"] });
  const canEdit = hasPermission(me, "clients.write");
  const connect = useMutation({
    mutationFn: () => api.send<{ url: string }>("POST", `/api/clients/${clientId}/qbo/connect`),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  const sync = useMutation({ mutationFn: () => api.send<{ accounts: number }>("POST", `/api/clients/${clientId}/qbo/sync`), onSuccess: (r) => toast({ title: "QuickBooks refreshed", description: `${r.accounts} accounts updated from QuickBooks.` }) });
  const disconnect = useMutation({ mutationFn: () => api.send("POST", `/api/clients/${clientId}/qbo/disconnect`) });
  const flag = useMutation({ mutationFn: (v: { id: string; isSuspense: boolean }) => api.send("PATCH", `/api/clients/${clientId}/qbo/accounts/${v.id}`, { isSuspense: v.isSuspense }) });
  const [showAccounts, setShowAccounts] = useState(false);
  if (isLoading || !data) return <Loading />;
  const banner =
    result === "connected"
      ? "QuickBooks Online is connected."
      : result === "cancelled"
        ? "The QuickBooks connection was cancelled."
        : result === "expired"
          ? "The connection request expired. Please try again."
          : result === "in_use_firm"
            ? "That QuickBooks company is already connected as the firm's own books. Choose a different company."
          : result === "error"
            ? "QuickBooks could not be connected. Please try again."
            : null;

  return (
    <div className="space-y-3">
      {banner && <p className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm">{banner}</p>}
      {!data.available ? (
        <p className="text-sm text-muted-foreground">The QuickBooks Online integration is not configured. Contact your administrator.</p>
      ) : !data.status || data.status === "disconnected" ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">QuickBooks Online is not connected for this client.</p>
          {canEdit && (
            <Button onClick={() => connect.mutate()} disabled={connect.isPending}>
              Connect QuickBooks
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {data.status === "connected" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium">{data.companyName ?? "QuickBooks company"}</p>
              <p className="text-xs text-muted-foreground">
                {data.status === "connected" ? `Last refreshed ${data.lastSyncedAt ? formatDateTime(data.lastSyncedAt) : "—"}` : "Connection expired — reconnect required"}
                {data.refreshExpiresAt && ` · Authorization valid until ${formatDate(data.refreshExpiresAt)}`}
              </p>
              {data.lastError && <p className="text-xs text-destructive">{data.lastError}</p>}
            </div>
            {canEdit && data.status === "connected" && (
              <Button size="sm" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
                <RefreshCw className="h-4 w-4" /> Refresh from QuickBooks
              </Button>
            )}
            {canEdit && data.status === "needs_reconnect" && (
              <Button size="sm" onClick={() => connect.mutate()}>
                Reconnect
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: "Disconnect QuickBooks Online?", description: "ClientFlow's access to this client's company is also revoked at Intuit. You can reconnect later.", confirmLabel: "Disconnect", destructive: true })) && disconnect.mutate()}>
                Disconnect
              </Button>
            )}
          </div>
          <button className="text-sm font-medium text-primary hover:underline" onClick={() => setShowAccounts((v) => !v)}>
            {showAccounts ? "Hide" : "Show"} chart of accounts ({data.accounts?.length ?? 0})
          </button>
          {showAccounts && (
            <div className="max-h-80 overflow-y-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5">Account</th>
                    <th className="px-3 py-1.5">Type</th>
                    <th className="px-3 py-1.5 text-right">Balance</th>
                    <th className="px-3 py-1.5" title="Transactions posted to this account are routed to the client for review">Client review</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts?.map((a) => (
                    <tr key={a.id} className={`border-t border-border ${a.active ? "" : "text-muted-foreground"}`}>
                      <td className="px-3 py-1">{a.fullyQualifiedName}</td>
                      <td className="px-3 py-1">{a.accountType}</td>
                      <td className="px-3 py-1 text-right tabular">{a.currentBalanceCents === null ? "—" : formatCents(a.currentBalanceCents)}</td>
                      <td className="px-3 py-1 text-center">
                        <input type="checkbox" aria-label={`Route ${a.name} transactions to client review`} checked={a.isSuspense} disabled={!canEdit} onChange={(e) => flag.mutate({ id: a.id, isSuspense: e.target.checked })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

type Question = {
  id: string;
  txnType: "Purchase" | "Deposit";
  txnDate: string;
  amountCents: number;
  payee: string | null;
  description: string | null;
  bankAccount: string | null;
  status: "open" | "answered" | "posted" | "dismissed" | "changed_in_qbo";
  isCredit: boolean;
  answerText: string | null;
  answerAccountQboId: string | null;
  receiptDocumentId: string | null;
  note: string | null;
};
type QuestionsData = { questions: Question[]; categories: Record<"Purchase" | "Deposit", Array<{ qboId: string; name: string }>>; accounts: Array<{ qboId: string; name: string; type: string }> };

const Q_STATUS: Record<Question["status"], string> = { open: "Awaiting client", answered: "Client responded", posted: "Posted", dismissed: "Dismissed", changed_in_qbo: "Resolved in QuickBooks" };

export function QuestionsPanel({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<QuestionsData>({ queryKey: ["/api/clients/", clientId, "/qbo/questions"] });
  const [range, setRange] = useState(lastMonth());
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const canEdit = hasPermission(me, "requests.write");
  const pull = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ created: number; resolved: number; open: number; skippedForeign: number }>("POST", `/api/clients/${clientId}/qbo/questions/pull`, range),
    onSuccess: (r) =>
      toast({
        title: "Transactions retrieved",
        description: `${r.created} new, ${r.resolved} resolved in QuickBooks, ${r.open} awaiting the client.${r.skippedForeign ? ` ${r.skippedForeign} foreign-currency transaction${r.skippedForeign > 1 ? "s were" : " was"} skipped; categorize ${r.skippedForeign > 1 ? "them" : "it"} in QuickBooks.` : ""}`,
      }),
  });
  const link = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ url: string; emailed: number }>("POST", `/api/clients/${clientId}/qbo/questions/link`),
    onSuccess: (r) => {
      setLinkUrl(r.url);
      toast({ title: "Client review link created", description: r.emailed ? `Emailed to ${r.emailed} portal user${r.emailed > 1 ? "s" : ""}.` : "Copy the link and send it to the client." });
    },
  });
  const post = useMutation({
    mutationFn: (v: { id: string; accountQboId?: string }) => api.send("POST", `/api/qbo/questions/${v.id}/post`, v.accountQboId ? { accountQboId: v.accountQboId } : {}),
    onSuccess: () => toast({ title: "Posted to QuickBooks" }),
  });
  const dismiss = useMutation({ mutationFn: (id: string) => api.send("POST", `/api/qbo/questions/${id}/dismiss`, {}) });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Posts the selected transactions one at a time, so one rejected write doesn't stop the rest.
  const bulk = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      let posted = 0;
      const failed: string[] = [];
      for (const q of data!.questions.filter((x) => selected.has(x.id))) {
        const accountQboId = choice[q.id] ?? q.answerAccountQboId;
        if (!accountQboId) continue;
        try {
          await api.send("POST", `/api/qbo/questions/${q.id}/post`, { accountQboId });
          posted++;
        } catch (e) {
          failed.push(`${q.payee ?? formatDate(q.txnDate)}: ${errorMessage(e)}`);
        }
      }
      return { posted, failed };
    },
    onSuccess: (r) => {
      setSelected(new Set());
      toast({ title: `${r.posted} transaction${r.posted === 1 ? "" : "s"} posted to QuickBooks`, description: r.failed.length ? `Not posted: ${r.failed.join("; ")}` : undefined, variant: r.failed.length ? "destructive" : undefined });
    },
  });
  const revoke = useMutation({
    mutationFn: () => api.send<{ revoked: number }>("POST", `/api/clients/${clientId}/qbo/questions/link/revoke`),
    onSuccess: (r) => {
      setLinkUrl(null);
      toast({ title: r.revoked ? "Client link deactivated" : "No active client link" });
    },
  });
  const live = (data?.questions ?? []).filter((q) => q.status === "open" || q.status === "answered");
  const done = (data?.questions ?? []).filter((q) => !(q.status === "open" || q.status === "answered"));
  const livePaged = usePaged(live);
  const donePaged = usePaged(done);
  if (isLoading || !data) return <Loading />;
  const name = (qboId: string | null) => data.accounts.find((a) => a.qboId === qboId)?.name ?? null;
  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="q-from">From</Label>
            <Input id="q-from" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="q-to">To</Label>
            <Input id="q-to" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
          </div>
          <Button variant="outline" onClick={() => pull.mutate()} disabled={pull.isPending}>
            Retrieve uncategorized transactions
          </Button>
          <Button onClick={() => link.mutate()} disabled={link.isPending || !live.length}>
            Send to client for review
          </Button>
          <Button variant="ghost" onClick={async () => (await ask({ title: "Deactivate the client review link?", description: "The client can still respond in the portal.", confirmLabel: "Deactivate link" })) && revoke.mutate()}>
            Deactivate client link
          </Button>
        </div>
      )}
      {(pull.error || link.error) && <p className="text-sm text-destructive">{errorMessage(pull.error ?? link.error)}</p>}
      {linkUrl && (
        <div className="flex gap-2 rounded-lg border border-accent/60 bg-accent/10 p-3">
          <Input readOnly value={linkUrl} onFocus={(e) => e.target.select()} aria-label="Client link" />
          <Button variant="outline" aria-label="Copy link" onClick={() => navigator.clipboard.writeText(linkUrl)}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      )}
      {!live.length ? (
        <Empty>No transactions are awaiting review. Transactions posted to accounts marked for client review appear here.</Empty>
      ) : (
        <div className="space-y-2">
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">{selected.size ? `${selected.size} selected` : "Select transactions with a category to post them together."}</span>
              {selected.size > 0 && (
                <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate()}>
                  Post {selected.size} to QuickBooks
                </Button>
              )}
            </div>
          )}
          <TableScroll label="Client questions table" className="rounded-lg border border-border">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  {canEdit && (
                    <th className="w-8 px-3 py-2">
                      <span className="sr-only">Select</span>
                    </th>
                  )}
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Payee</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Client response</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  {canEdit && (
                    <th className="px-3 py-2">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {livePaged.items.map((q) => {
                  const options = data.accounts.filter((a) => (q.txnType === "Purchase" ? ["Expense", "Cost of Goods Sold", "Other Expense", "Other Current Asset", "Fixed Asset"] : ["Income", "Other Income", "Other Current Liability", "Equity"]).includes(a.type));
                  const pick = choice[q.id] ?? q.answerAccountQboId ?? "";
                  const signed = q.txnType === "Deposit" || q.isCredit ? formatCents(q.amountCents) : `(${formatCents(q.amountCents)})`;
                  return (
                    <tr key={q.id} className="align-top">
                      {canEdit && (
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            aria-label={`Select ${q.payee ?? "transaction"} ${formatDate(q.txnDate)}`}
                            disabled={!pick}
                            checked={selected.has(q.id) && !!pick}
                            onChange={(e) => {
                              const next = new Set(selected);
                              if (e.target.checked) next.add(q.id);
                              else next.delete(q.id);
                              setSelected(next);
                            }}
                          />
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-2.5 tabular">{formatDate(q.txnDate)}</td>
                      <td className="min-w-[14rem] px-3 py-2.5">
                        <p className="font-medium">{q.payee ?? "—"}</p>
                        <p className="max-w-[18rem] truncate text-xs text-muted-foreground">
                          {q.txnType === "Deposit" ? "Deposit" : q.isCredit ? "Card credit" : "Payment"}
                          {q.bankAccount && ` · ${q.bankAccount}`}
                        </p>
                        {q.description && (
                          <p className="max-w-[18rem] truncate text-xs text-muted-foreground" title={q.description}>
                            {q.description}
                          </p>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular">{signed}</td>
                      <td className="max-w-56 px-3 py-2.5 text-xs">
                        {q.status === "answered" ? (
                          <>
                            <span>“{q.answerText ?? "—"}”</span>
                            {q.answerAccountQboId && <span className="block text-muted-foreground">Suggested: {name(q.answerAccountQboId)}</span>}
                            {q.receiptDocumentId && <span className="block text-muted-foreground">Receipt attached</span>}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {canEdit ? (
                          <select aria-label="Category" className={`${select} w-48`} value={pick} onChange={(e) => setChoice({ ...choice, [q.id]: e.target.value })}>
                            <option value="">Select category</option>
                            {options.map((a) => (
                              <option key={a.qboId} value={a.qboId}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          (name(pick) ?? "—")
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                        <span className={q.status === "answered" ? "font-medium text-primary" : "text-muted-foreground"}>{Q_STATUS[q.status]}</span>
                      </td>
                      {canEdit && (
                        <td className="whitespace-nowrap px-3 py-2.5 text-right">
                          <Button size="sm" variant="outline" disabled={!pick || post.isPending} onClick={() => post.mutate({ id: q.id, accountQboId: pick })}>
                            Post
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(q.id)}>
                            Dismiss
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
          <Pager paged={livePaged} noun="transactions" />
        </div>
      )}
      {done.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">Resolved ({done.length})</summary>
          <ul className="mt-2 space-y-1">
            {donePaged.items.map((q) => (
              <li key={q.id} className="flex flex-wrap gap-x-3 text-xs">
                <span>{formatDate(q.txnDate)}</span>
                <span>{formatCents(q.amountCents)}</span>
                <span>{q.payee}</span>
                <span className="text-muted-foreground">{Q_STATUS[q.status]}{q.note ? ` — ${q.note}` : ""}</span>
              </li>
            ))}
          </ul>
          <Pager paged={donePaged} noun="resolved transactions" compact className="mt-2" />
        </details>
      )}
    </div>
  );
}

// --- Close panel (on a bookkeeping close work item) -----------------------------------------

type Check = { checkKey: string; status: "pass" | "warn" | "fail" | "pending" | "error"; summary: string; checkedAt: string };
type CloseView = { period: string; start: string; end: string; label: string; status: string; qboConnected: boolean; payrollAccountIds: string[]; checks: Check[] };

const CHECK_LABELS: Record<string, string> = {
  report_integrity: "Financial statements tie to the trial balance",
  uncategorized: "No uncategorized transactions",
  undeposited_funds: "Undeposited funds cleared",
  negative_assets: "No negative asset balances",
  ar_aging: "Accounts receivable aging reviewed",
  ap_aging: "Accounts payable aging reviewed",
  vendors_w9: "W-9s on file for 1099 vendors",
  payroll_tieout: "Payroll expense ties to payroll provider",
  reconciled: "Bank and credit card accounts reconciled",
  recurring_entries: "Recurring adjusting entries posted",
};

function CheckIcon({ status }: { status: Check["status"] }) {
  if (status === "pass") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "warn") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (status === "fail" || status === "error") return <XCircle className="h-4 w-4 text-destructive" />;
  return <CircleDashed className="h-4 w-4 text-muted-foreground" />;
}

export function ClosePanel({ engagementId, clientId, me }: { engagementId: string; clientId: string; me: Me }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<CloseView>({ queryKey: ["/api/engagements/", engagementId, "/close"], retry: false });
  const { data: qbo } = useQuery<QboStatus>({ queryKey: ["/api/clients/", clientId, "/qbo"] });
  const canEdit = hasPermission(me, "engagements.write");
  const isAdmin = me.role === "owner" || me.role === "admin";
  const run = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/engagements/${engagementId}/close/checks`) });
  const recon = useMutation({ mutationFn: () => api.send("POST", `/api/engagements/${engagementId}/close/reconciled`) });
  const [payroll, setPayroll] = useState<{ ids: string[]; total: string } | null>(null);
  const savePayroll = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      let cents: number;
      try {
        cents = parseDollars(payroll!.total);
      } catch {
        throw new ApiError(400, "invalid_amount", "Enter the provider total in dollars and cents, for example 4,306.00");
      }
      return api.send("POST", `/api/engagements/${engagementId}/close/payroll`, { accountIds: payroll!.ids, providerTotalCents: cents });
    },
    onSuccess: () => setPayroll(null),
  });
  const [override, setOverride] = useState("");
  const lock = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ closingDate: string }>("POST", `/api/engagements/${engagementId}/close/lock`, override.trim() ? { overrideReason: override.trim() } : {}),
    onSuccess: (r) => toast({ title: "Period closed", description: `QuickBooks closing date set to ${formatDate(r.closingDate)}.` }),
    // Locking re-runs the checks, so show their fresh results when it's refused.
    onError: () => qc.invalidateQueries({ queryKey: ["/api/engagements/", engagementId, "/close"] }),
  });
  const [commentary, setCommentary] = useState("");
  const pkg = useMutation({
    meta: { inlineError: true },
    // confirmNumbers: figures staff checked that aren't one of the month's QuickBooks figures.
    mutationFn: (confirmNumbers: string[]) => api.send("POST", `/api/engagements/${engagementId}/close/package`, { commentary, confirmNumbers }),
    onSuccess: () => toast({ title: "Financial statements published", description: "Saved to the client's Financial Statements folder in the portal." }),
  });
  // Claude drafts from the closed month's QuickBooks figures; staff edit it before publishing.
  const draft = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ commentary: string }>("POST", `/api/engagements/${engagementId}/close/commentary/draft`),
    onSuccess: (r) => setCommentary(r.commentary),
  });
  const toConfirm = pkg.error instanceof ApiError && pkg.error.code === "unmatched_numbers" ? ((pkg.error.details as { unmatched?: string[] } | undefined)?.unmatched ?? []) : [];
  if (isLoading) return <Loading tall />;
  if (error || !data) return <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>;
  const order = Object.keys(CHECK_LABELS);
  const checks = [...data.checks].sort((a, b) => order.indexOf(a.checkKey) - order.indexOf(b.checkKey));
  const blocking = checks.filter((c) => c.status !== "pass" && c.status !== "warn");
  const expenseAccounts = (qbo?.accounts ?? []).filter((a) => a.active && !a.isSuspense && /Expense|Cost of Goods Sold/.test(a.accountType));
  const closed = data.status === "completed";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">
          {data.label}: {formatDate(data.start)} – {formatDate(data.end)}
        </p>
        {canEdit && !closed && (
          <Button size="sm" variant="outline" onClick={() => run.mutate()} disabled={run.isPending || !data.qboConnected}>
            <RefreshCw className="h-4 w-4" /> {checks.length ? "Re-run close checklist" : "Run close checklist"}
          </Button>
        )}
        {!data.qboConnected && <span className="text-xs text-warning">Connect QuickBooks Online on the client's Accounting Services tab first.</span>}
      </div>
      {run.error && <p className="text-sm text-destructive">{errorMessage(run.error)}</p>}
      {checks.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {checks.map((c) => (
            <li key={c.checkKey} className="flex flex-wrap items-start gap-x-3 gap-y-2 px-3 py-2 sm:flex-nowrap">
              <CheckIcon status={c.status} />
              <div className="min-w-0 flex-1 basis-48">
                <p className="text-sm font-medium">{CHECK_LABELS[c.checkKey] ?? c.checkKey}</p>
                <p className="text-xs text-muted-foreground">{c.summary}</p>
              </div>
              {canEdit && !closed && c.checkKey === "reconciled" && c.status !== "pass" && (
                <Button size="sm" variant="outline" className="ml-8 sm:ml-0" onClick={() => recon.mutate()}>
                  Confirm reconciliations complete
                </Button>
              )}
              {canEdit && !closed && c.checkKey === "payroll_tieout" && (
                <Button size="sm" variant="ghost" className="ml-8 sm:ml-0" onClick={() => setPayroll({ ids: data.payrollAccountIds, total: "" })}>
                  Enter payroll provider total
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {payroll && (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-sm font-medium">Payroll tie-out</p>
          <p className="text-xs text-muted-foreground">Select the QuickBooks accounts that hold payroll costs and enter the payroll provider's total for the period (from the Gusto or QuickBooks Payroll report).</p>
          <div className="grid max-h-40 gap-1 overflow-y-auto sm:grid-cols-2">
            {expenseAccounts.map((a) => (
              <label key={a.qboId} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={payroll.ids.includes(a.qboId)}
                  onChange={(e) => setPayroll({ ...payroll, ids: e.target.checked ? [...payroll.ids, a.qboId] : payroll.ids.filter((x) => x !== a.qboId) })}
                />
                {a.fullyQualifiedName}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Input aria-label="Provider total" inputMode="decimal" placeholder="$0.00" className="max-w-48" value={payroll.total} onChange={(e) => setPayroll({ ...payroll, total: e.target.value })} />
            <Button size="sm" disabled={!payroll.ids.length || !payroll.total || savePayroll.isPending} onClick={() => savePayroll.mutate()}>
              Compare
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPayroll(null)}>
              Cancel
            </Button>
          </div>
          {savePayroll.error && <p className="text-sm text-destructive">{errorMessage(savePayroll.error)}</p>}
        </div>
      )}
      {canEdit && !closed && checks.length > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          {blocking.length > 0 && isAdmin && (
            <Input aria-label="Reason for closing with exceptions" placeholder="Reason for closing with open exceptions (owners and admins only)" value={override} onChange={(e) => setOverride(e.target.value)} />
          )}
          <Button onClick={() => lock.mutate()} disabled={lock.isPending || (blocking.length > 0 && override.trim().length < 10)}>
            Close the period in QuickBooks
          </Button>
          {blocking.length > 0 && <p className="text-xs text-muted-foreground">{blocking.length} checklist item{blocking.length > 1 ? "s" : ""} not yet passed.</p>}
          {lock.error && <p className="text-sm text-destructive">{errorMessage(lock.error)}</p>}
        </div>
      )}
      {closed && (
        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-sm font-medium">Monthly financial statements</p>
          <Textarea aria-label="Commentary for the client" rows={6} placeholder="Commentary for the client: key changes this month and items to monitor" value={commentary} onChange={(e) => setCommentary(e.target.value)} />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={draft.isPending}
              onClick={() =>
                commentary.trim()
                  ? void ask({ title: "Replace your commentary?", description: "Claude's draft replaces what is in the box now.", confirmLabel: "Replace" }).then((ok) => ok && draft.mutate())
                  : draft.mutate()
              }
            >
              <Sparkles className="h-4 w-4" /> {draft.isPending ? "Drafting…" : "Draft with Claude"}
            </Button>
            <Button onClick={() => pkg.mutate([])} disabled={pkg.isPending}>
              Publish to client portal
            </Button>
          </div>
          {draft.isSuccess && !draft.isPending && <p className="text-xs text-muted-foreground">Draft ready: every figure in it was filled in from this month's QuickBooks reports. Read it and edit as needed before publishing.</p>}
          {draft.error && <p className="text-sm text-destructive">{errorMessage(draft.error)}</p>}
          {pkg.error && <p className="text-sm text-destructive">{errorMessage(pkg.error)}</p>}
          {toConfirm.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => pkg.mutate(toConfirm)} disabled={pkg.isPending}>
              I have checked these figures: publish
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// --- Close grid (staff dashboard) ---------------------------------------------------------------

type GridCell = { period: string; status: "not_started" | "waiting_on_client" | "in_progress" | "review" | "closed"; engagementId: string | null; daysBehind: number; openQuestions: number };
type Grid = { months: string[]; rows: Array<{ clientId: string; name: string; level: string | null; qbo: { status: string; lastSyncedAt: string | null; lastError: string | null } | null; answeredQuestions: number; closedPeriodChanges: number; cells: GridCell[] }> };

const CELL: Record<GridCell["status"], [string, string]> = {
  not_started: ["Not started", "bg-muted text-muted-foreground"],
  waiting_on_client: ["Awaiting client", "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"],
  in_progress: ["In progress", "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200"],
  review: ["In review", "bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-200"],
  closed: ["Closed", "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200"],
};

/** Creates the period's close work now (the background job also does it daily). Also on the Workflow tab, for people without the close grid. */
export function CreateClosesButton({ me, month }: { me: Me; month?: string }) {
  const { toast } = useToast();
  const gen = useMutation({
    mutationFn: () => api.send<{ created: number }>("POST", "/api/bookkeeping/closes/generate"),
    onSuccess: (r) =>
      toast({
        title: r.created ? `${r.created} close${r.created > 1 ? "s" : ""} created` : "All closes for the period already exist",
        description: r.created ? undefined : "A new client's first close is created after their first full month ends.",
      }),
  });
  if (!hasPermission(me, "engagements.write")) return null;
  return (
    <Button variant="outline" onClick={() => gen.mutate()} disabled={gen.isPending}>
      {month ? `Create ${formatPeriod(month)} closes` : "Create closes for the period just ended"}
    </Button>
  );
}

export function CloseGridPage({ me }: { me: Me }) {
  const { data, isLoading } = useQuery<Grid>({ queryKey: ["/api/bookkeeping/close-grid?months=6"] });
  const isAdmin = me.role === "owner" || me.role === "admin";
  const { data: usage } = useQuery<Array<{ month: string; reads: number }>>({ queryKey: ["/api/qbo/usage"], enabled: isAdmin });
  const thisMonth = new Date().toISOString().slice(0, 7);
  const reads = usage?.find((u) => u.month === thisMonth)?.reads ?? 0;
  const latest = (r: Grid["rows"][number]) => r.cells[r.cells.length - 1];
  const rows = data?.rows ?? [];
  const isLate = (r: Grid["rows"][number]) => r.cells.some((c) => c.daysBehind > 0 && c.status !== "closed");
  const isWaiting = (r: Grid["rows"][number]) => latest(r)?.status === "waiting_on_client";
  const late = rows.filter(isLate).length;
  const waiting = rows.filter(isWaiting).length;
  const [search, setSearch] = useState("");
  const [show, setShow] = useState<"all" | "late" | "waiting">("all");
  const shown = rows.filter((r) => (show === "all" || (show === "late" ? isLate(r) : isWaiting(r))) && matches(search, r.name));
  const paged = usePaged(shown, { resetKey: `${show}|${search}` });
  const toPost = rows.reduce((n, r) => n + r.answeredQuestions, 0);
  const latestMonth = data?.months[data.months.length - 1];
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Accounting Services", href: "/accounting" }]}
        title="Month-End Close"
        description="Close status for every bookkeeping client over the last six months."
        actions={
<CreateClosesButton me={me} month={latestMonth} />
        }
      />
      {data && (
        <StatRow
          stats={[
            { label: "Bookkeeping clients", value: rows.length },
            { label: "Closes past due", value: late, tone: late ? "danger" : "default" },
            { label: "Awaiting client responses", value: waiting, tone: waiting ? "warning" : "default" },
            { label: "Client responses to post", value: toPost, tone: toPost ? "warning" : "default" },
          ]}
        />
      )}
      {isLoading || !data ? (
        <Loading />
      ) : !rows.length ? (
        <Empty>No clients are engaged for bookkeeping services.</Empty>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <ListSearch value={search} onChange={setSearch} placeholder="Search clients" />
            <Segmented
              label="Show"
              value={show}
              onChange={setShow}
              options={[
                { value: "all", label: "All", count: rows.length },
                { value: "late", label: "Past due", count: late },
                { value: "waiting", label: "Awaiting client", count: waiting },
              ]}
            />
          </div>
          {!shown.length ? (
            <Empty>No clients match.</Empty>
          ) : (
        <TableScroll label="Monthly close by client table" className="rounded-lg border border-card-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-3 py-2.5 font-medium">QuickBooks</th>
                {data.months.map((m) => (
                  <th key={m} className="px-2 py-2.5 text-center font-medium">
                    {formatPeriod(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paged.items.map((r) => (
                <tr key={r.clientId} className="hover:bg-muted/30">
                  <td className="px-4 py-2.5">
                    <Link href={`/clients/${r.clientId}?tab=books`} className="font-medium hover:underline">
                      {r.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {r.level ?? "Bookkeeping"}
                      {r.answeredQuestions ? ` · ${r.answeredQuestions} client response${r.answeredQuestions > 1 ? "s" : ""} to post` : ""}
                    </p>
                    {r.closedPeriodChanges > 0 && (
                      <Link href={`/clients/${r.clientId}?tab=books&section=overview`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-900 hover:underline dark:bg-red-900/30 dark:text-red-200">
                        <AlertTriangle className="h-3 w-3" /> {r.closedPeriodChanges} change{r.closedPeriodChanges > 1 ? "s" : ""} to closed months
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {!r.qbo ? (
                      <span className="text-muted-foreground">Not connected</span>
                    ) : r.qbo.status === "connected" ? (
                      <span className={r.qbo.lastError ? "text-warning" : "text-success"}>{r.qbo.lastError ? "Refresh error" : "Connected"}</span>
                    ) : (
                      <span className="text-destructive">Reconnect required</span>
                    )}
                  </td>
                  {r.cells.map((c) => (
                    <td key={c.period} className="px-1.5 py-2.5 text-center">
                      {c.engagementId ? (
                        <Link href={`/engagements/${c.engagementId}`} className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${c.daysBehind > 0 && c.status !== "closed" ? "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200" : CELL[c.status][1]}`}>
                          {CELL[c.status][0]}
                          {c.daysBehind > 0 && c.status !== "closed" && ` · ${c.daysBehind} day${c.daysBehind > 1 ? "s" : ""} past due`}
                        </Link>
                      ) : (
                        <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ${CELL[c.status][1]}`}>{CELL[c.status][0]}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
          <Pager paged={paged} noun="clients" />
        </div>
      )}
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <p className="flex items-center gap-1">
          <ExternalLink className="h-3 w-3" /> Bank feed transactions still “For Review” in QuickBooks are not available to ClientFlow. Accept them in QuickBooks (to Uncategorized Expense or Income if unclear) and they will appear in Transaction Review.
        </p>
        {isAdmin && usage && <p>QuickBooks API usage this month: {reads.toLocaleString()} of 500,000 included read requests.</p>}
      </div>
    </>
  );
}

/** The client record's Accounting Services sections (shown nested in the client's section list). */
export const BOOKS_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "bookkeeping", label: "Bookkeeping" },
  { key: "review", label: "Transaction Review" },
  { key: "bills", label: "Bills & Payables" },
  { key: "invoicing", label: "Invoicing & Receivables" },
  { key: "adjusting", label: "Adjusting Entries" },
  { key: "reconciliations", label: "Reconciliations" },
  { key: "statements", label: "Financial Statements" },
  { key: "lists", label: "Lists" },
  { key: "compliance", label: "1099 Compliance" },
];

export function BooksTab({ clientId, me, section }: { clientId: string; me: Me; section: string }) {
  const sec = BOOKS_SECTIONS.some((x) => x.key === section) ? section : "overview";
  const { data } = useQuery<QboStatus>({ queryKey: ["/api/clients/", clientId, "/qbo"] });
  const connected = !!data?.connected;
  const needsQbo = <Empty>Connect QuickBooks Online under Overview first.</Empty>;
  return (
    <div className="space-y-6">
      {sec === "overview" && (
        <>
          <Section title="QuickBooks Online">
            <QboConnection clientId={clientId} me={me} />
          </Section>
          {connected && (
            <Section title="Changes in QuickBooks" description="Read from QuickBooks with every refresh. A change to a closed month stays an alert until someone acknowledges it.">
              <QboAlerts clientId={clientId} me={me} />
            </Section>
          )}
          {connected && <QboChanges clientId={clientId} />}
        </>
      )}
      {sec === "bookkeeping" && (connected ? (
        <Section title="Bookkeeping" description="Every transaction posted in QuickBooks. Expenses, deposits, bills and journal entries open here to recategorize, split, classify and attach receipts; changes save straight to QuickBooks.">
          <BookkeepingRegister clientId={clientId} me={me} />
        </Section>
      ) : needsQbo)}
      {sec === "review" && (
        <Section title="Transaction Review" description="Uncategorized transactions, client responses and posting to QuickBooks.">
          <QuestionsPanel clientId={clientId} me={me} />
        </Section>
      )}
      {sec === "bills" && (connected ? (
        <Section title="Bills & Payables" description="Bills read automatically and posted to QuickBooks with the bill attached, open bills and payments, and payables by age.">
          <BillsPayables clientId={clientId} me={me} />
        </Section>
      ) : needsQbo)}
      {sec === "invoicing" && (connected ? (
        <Section title="Invoicing & Receivables" description="Invoices created and emailed from the client's QuickBooks, customer payments, collections and receivables by age.">
          <InvoicingReceivables clientId={clientId} me={me} />
        </Section>
      ) : needsQbo)}
      {sec === "adjusting" && (connected ? <AdjustingEntries clientId={clientId} me={me} /> : needsQbo)}
      {sec === "reconciliations" && (connected ? (
        <Section title="Reconciliations" description="A workpaper for each bank, credit card and loan account every month, signed by the preparer and a reviewer.">
          <Reconciliations clientId={clientId} me={me} />
        </Section>
      ) : needsQbo)}
      {sec === "statements" && (connected ? (
        <Section title="Financial Statements" description="Drawn live from QuickBooks: statements with comparisons, the trial balance, the general ledger, and results by class or location.">
          <BooksStatements clientId={clientId} />
        </Section>
      ) : needsQbo)}
      {sec === "lists" && (connected ? (
        <Section title="Lists" description="The client's chart of accounts, vendors, customers, products and services, classes and locations in QuickBooks.">
          <BookkeepingLists clientId={clientId} me={me} />
        </Section>
      ) : needsQbo)}
      {sec === "compliance" && (
        connected ? (
          <Section title="1099 Compliance" description="What each vendor was paid in the year, who needs a Form 1099-NEC, and their W-9s.">
            <Compliance1099 clientId={clientId} me={me} />
          </Section>
        ) : (
          needsQbo
        )
      )}
    </div>
  );
}
